// src/pages/pmc/PmcLogin.js
import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { auth, db } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";

const cx = (...xs) => xs.filter(Boolean).join(" ");
const PMC_USERS_COLLECTION = "pmc_users";
const CEO_EMAIL = "ceo@shineagri.org";
const PMC_PROFILE_COLLECTIONS = ["pmc_users", "nafed_users", "users"];
const ALLOWED_PMC_ROLES = [
  "CEO",
  "ADMIN",
  "PMC",
  "STAFF",
  "PMC_ADMIN",
  "PMC_ENGINEER",
];

const cleanEmail = (value) => String(value || "").trim().toLowerCase();
const normalizeRole = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

const normalizeStatus = (value) =>
  String(value || "ACTIVE").trim().toUpperCase();

const ceoFallbackProfile = (user) => ({
  uid: user.uid,
  name: "Nareshbhai P Chaudhary",
  email: CEO_EMAIL,
  role: "CEO",
  status: "ACTIVE",
  isActive: true,
  modules: ["ALL"],
  permissions: ["ALL"],
  authFallback: true,
});

async function findProfileByEmail(emailValue) {
  const normalizedEmail = cleanEmail(emailValue);
  if (!normalizedEmail) return null;

  for (const collectionName of PMC_PROFILE_COLLECTIONS) {
    try {
      const profileQuery = query(
        collection(db, collectionName),
        where("email", "==", normalizedEmail)
      );
      const snapshot = await getDocs(profileQuery);
      if (!snapshot.empty) {
        const profileDoc = snapshot.docs[0];
        return {
          id: profileDoc.id,
          collectionName,
          data: profileDoc.data() || {},
        };
      }
    } catch (error) {
      console.warn(
        `PMC email profile check skipped for ${collectionName}:`,
        error?.code || error?.message || error
      );
    }
  }

  return null;
}

async function resolvePmcProfile(user) {
  if (!user?.uid) throw new Error("AUTH USER NOT FOUND");

  const normalizedEmail = cleanEmail(user.email);

  for (const collectionName of PMC_PROFILE_COLLECTIONS) {
    try {
      const snapshot = await getDoc(doc(db, collectionName, user.uid));
      if (snapshot.exists()) {
        return snapshot.data() || {};
      }
    } catch (error) {
      console.warn(
        `PMC UID profile check skipped for ${collectionName}:`,
        error?.code || error?.message || error
      );
    }
  }

  const emailProfile = await findProfileByEmail(normalizedEmail);
  if (emailProfile) {
    const profileData = emailProfile.data || {};

    // Migration is best-effort. A denied write must not destroy a valid
    // authenticated session or create a login redirect loop.
    if (
      emailProfile.collectionName === PMC_USERS_COLLECTION &&
      emailProfile.id !== user.uid
    ) {
      try {
        await setDoc(
          doc(db, PMC_USERS_COLLECTION, user.uid),
          {
            ...profileData,
            uid: user.uid,
            email: normalizedEmail,
            migratedFromDocId: emailProfile.id,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (error) {
        console.warn(
          "PMC UID profile migration skipped:",
          error?.code || error?.message || error
        );
      }
    }

    return profileData;
  }

  try {
    const tokenResult = await user.getIdTokenResult();
    const claimRole = normalizeRole(
      tokenResult?.claims?.role || tokenResult?.claims?.userRole
    );
    if (ALLOWED_PMC_ROLES.includes(claimRole)) {
      return {
        email: normalizedEmail,
        role: claimRole,
        status: "ACTIVE",
        isActive: true,
        modules: tokenResult?.claims?.modules || [],
        permissions: tokenResult?.claims?.permissions || [],
        profileSource: "firebase-custom-claims",
      };
    }
  } catch (error) {
    console.warn("PMC custom-claim check skipped:", error?.message || error);
  }

  if (normalizedEmail === CEO_EMAIL) {
    const fallback = ceoFallbackProfile(user);
    try {
      await setDoc(
        doc(db, PMC_USERS_COLLECTION, user.uid),
        {
          ...fallback,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      console.warn(
        "CEO PMC profile sync skipped; authenticated fallback used:",
        error?.code || error?.message || error
      );
    }
    return fallback;
  }

  return null;
}

async function validatePmcProfile(user) {
  const normalizedEmail = cleanEmail(user?.email);
  const profile = await resolvePmcProfile(user);

  if (!profile) {
    throw new Error(`PMC PROFILE NOT FOUND UID: ${user?.uid || "unknown"}`);
  }

  if (profile.email && cleanEmail(profile.email) !== normalizedEmail) {
    throw new Error("PMC EMAIL MISMATCH");
  }

  const status = normalizeStatus(profile.status);
  if (
    profile.isActive === false ||
    ["INACTIVE", "BLOCKED", "SUSPENDED", "REJECTED"].includes(status)
  ) {
    throw new Error("PMC USER INACTIVE");
  }

  let role = normalizeRole(profile.role);
  if (normalizedEmail === CEO_EMAIL) role = "CEO";
  if (!role && Array.isArray(profile.permissions)) role = "STAFF";

  if (!ALLOWED_PMC_ROLES.includes(role)) {
    throw new Error("NO DASHBOARD PERMISSION ASSIGNED");
  }

  const finalUser = {
    ...profile,
    uid: user.uid,
    email: normalizedEmail,
    role,
    status,
    sessionValidatedAt: Date.now(),
  };

  localStorage.setItem("pmcUser", JSON.stringify(finalUser));
  return finalUser;
}

export default function PmcLogin() {
  const navigate = useNavigate();
  const manualLoginRef = useRef(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      try {
        if (!u) {
          localStorage.removeItem("pmcUser");
          setChecking(false);
          return;
        }

        // signInWithEmailAndPassword also triggers this observer. During a
        // manual login the submit handler owns validation, preventing two
        // competing checks where one could sign out the other's session.
        if (manualLoginRef.current) return;

        await validatePmcProfile(u);
        navigate("/pmc/dashboard", { replace: true });
      } catch (err) {
        console.error("AUTO LOGIN CHECK ERROR:", {
          code: err?.code || "PMC_SESSION_CHECK_FAILED",
          message: err?.message || String(err),
        });
        localStorage.removeItem("pmcUser");
        if (auth.currentUser?.uid === u?.uid) await signOut(auth);
        setChecking(false);
      }
    });

    return () => unsub();
  }, [auth, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();

    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail || !password.trim()) {
      toast.error("Please enter email & password.");
      return;
    }

    try {
      setLoading(true);
      manualLoginRef.current = true;

      const cred = await signInWithEmailAndPassword(
        auth,
        cleanEmail,
        password.trim()
      );

      await validatePmcProfile(cred.user);

      toast.success("Login successful.");
      navigate("/pmc/dashboard", { replace: true });
    } catch (err) {
      console.error("FULL LOGIN ERROR:", {
        code: err?.code || "PMC_LOGIN_FAILED",
        message: err?.message || String(err),
      });
      localStorage.removeItem("pmcUser");
      if (auth.currentUser) await signOut(auth);

      toast.error(
        err?.message?.includes("PMC PROFILE")
          ? "PMC profile not found. Please create user in pmc_users."
          : err?.message?.includes("MISMATCH")
          ? "Email mismatch in PMC profile."
          : err?.message?.includes("INACTIVE")
          ? "This PMC user is inactive."
          : err?.message?.includes("PERMISSION")
          ? "No dashboard permission assigned."
          : "Invalid email or password."
      );
    } finally {
      manualLoginRef.current = false;
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="rounded-2xl border border-emerald-400/30 bg-white/10 px-6 py-4 text-sm font-bold text-white shadow-2xl backdrop-blur">
          Checking secure session...
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950">
      <Toaster position="top-center" />

      <div className="absolute inset-0 bg-[linear-gradient(120deg,#020617,#064e3b,#0f172a,#082f49,#020617)] bg-[length:400%_400%] animate-[gradientMove_14s_ease_infinite]" />

      <div className="absolute inset-0 opacity-[0.18]">
        <div className="h-full w-full bg-[linear-gradient(to_right,rgba(255,255,255,0.25)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.25)_1px,transparent_1px)] bg-[size:48px_48px]" />
      </div>

      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-24 top-10 h-80 w-80 rounded-full bg-emerald-400/20 blur-3xl animate-pulse" />
        <div className="absolute right-0 top-1/4 h-96 w-96 rounded-full bg-sky-400/20 blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-1/3 h-96 w-96 rounded-full bg-teal-300/20 blur-3xl animate-pulse" />
        <div className="absolute left-[12%] top-[18%] h-3 w-3 rounded-full bg-emerald-300/70 animate-bounce" />
        <div className="absolute right-[18%] top-[22%] h-2 w-2 rounded-full bg-sky-300/70 animate-ping" />
        <div className="absolute bottom-[20%] left-[22%] h-2.5 w-2.5 rounded-full bg-teal-200/70 animate-pulse" />
      </div>

      <style>{`
        @keyframes gradientMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes floatCard {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes scanLine {
          0% { transform: translateX(-100%); opacity: 0; }
          35% { opacity: .6; }
          100% { transform: translateX(100%); opacity: 0; }
        }
      `}</style>

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <div className="grid w-full max-w-6xl overflow-hidden rounded-[2.2rem] border border-white/15 bg-white/10 shadow-2xl backdrop-blur-xl lg:grid-cols-[1.1fr_0.9fr]">
          <div className="relative hidden min-h-[650px] overflow-hidden bg-slate-950/60 p-10 text-white lg:block">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(52,211,153,0.28),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(14,165,233,0.25),transparent_35%)]" />
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-300 to-transparent animate-[scanLine_4s_linear_infinite]" />

            <div className="relative z-10 flex h-full flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-3 rounded-full border border-emerald-300/30 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-emerald-100">
                  <span className="h-2 w-2 rounded-full bg-emerald-300 animate-pulse" />
                  Live PMC Secure Portal
                </div>

                <h1 className="mt-8 max-w-xl text-4xl font-black leading-tight tracking-tight">
                  Shine Agri Project Management Console
                </h1>

                <p className="mt-4 max-w-lg text-sm leading-6 text-slate-300">
                  Centralized access for PACS applications, site visits, BOQ,
                  daily progress, RA bills, LOA and payments.
                </p>

                <div className="mt-8 grid grid-cols-2 gap-4">
                  {[
                    ["PACS", "Applications"],
                    ["BOQ", "Tender Setup"],
                    ["RA", "Bill Approval"],
                    ["LOA", "Work Order"],
                  ].map(([a, b]) => (
                    <div
                      key={a}
                      className="rounded-3xl border border-white/10 bg-white/10 p-5 shadow-xl backdrop-blur"
                      style={{ animation: "floatCard 5s ease-in-out infinite" }}
                    >
                      <div className="text-2xl font-black text-emerald-200">
                        {a}
                      </div>
                      <div className="mt-1 text-xs font-semibold text-slate-300">
                        {b}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/10 p-5 backdrop-blur">
                <div className="text-xs font-black uppercase tracking-[0.2em] text-emerald-200">
                  System Time
                </div>
                <div className="mt-2 text-2xl font-black">
                  {time.toLocaleTimeString("en-IN")}
                </div>
                <div className="mt-1 text-xs text-slate-300">
                  {time.toLocaleDateString("en-IN", {
                    weekday: "long",
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="relative bg-white/95 p-6 sm:p-10">
            <div className="mx-auto flex max-w-md flex-col justify-center">
              <div className="mb-8 text-center">
                <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-emerald-700 to-teal-700 text-lg font-black text-white shadow-lg">
                  PMC
                </div>

                <div className="mt-5 text-[11px] font-black uppercase tracking-[0.28em] text-emerald-700">
                  Project Management Console
                </div>

                <h2 className="mt-2 text-2xl font-black text-slate-950">
                  Admin / Staff Login
                </h2>

                <p className="mt-2 text-xs leading-5 text-slate-500">
                  Enter official credentials to access your assigned modules.
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="mb-1 block text-xs font-black text-slate-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                    placeholder="ceo@shineagri.org"
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-black text-slate-700">
                    Password
                  </label>

                  <div className="relative">
                    <input
                      type={showPass ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-20 text-sm font-semibold text-slate-800 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                      placeholder="Enter password"
                      autoComplete="current-password"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPass((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full px-3 py-1 text-[11px] font-black text-emerald-700 hover:bg-emerald-50"
                    >
                      {showPass ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={cx(
                    "w-full rounded-2xl px-4 py-3 text-sm font-black text-white shadow-lg transition",
                    loading
                      ? "bg-emerald-300 cursor-not-allowed"
                      : "bg-gradient-to-r from-emerald-700 to-teal-700 hover:from-emerald-800 hover:to-teal-800"
                  )}
                >
                  {loading ? "Verifying access..." : "Login to Dashboard"}
                </button>
              </form>

              <div className="mt-5 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-[11px] leading-5 text-emerald-900">
                <b>Access Note:</b> Staff users will see only assigned modules.
                CEO / Admin users can access full PMC dashboard.
              </div>

              <div className="mt-6 text-center text-xs text-slate-500">
                <Link to="/" className="font-bold text-emerald-700 underline">
                  ← Back to Main Site
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
